import React, { useEffect, useState } from 'react';
import { TableLoading } from '../Loading';
import styles from './PermissionsPage.module.css';
import {
  getAllPermissions,
  Permission,
} from '../../services/permissions.service';
import {
  getAllRoles,
  Role,
  removePermissionFromRole,
} from '../../services/roles.service';
import NewRoleModal from './NewRoleModal';
import AddPermissionsModal from './AddPermissionsModal';

const PermissionsPage: React.FC = () => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewRoleModal, setShowNewRoleModal] = useState(false);
  const [showAddPermissionsModal, setShowAddPermissionsModal] = useState(false);
  const [removingPermission, setRemovingPermission] = useState<{
    roleId: number;
    permissionId: number;
  } | null>(null);

  const permissionColor = (permission: string) => {
    // Color coding based on permission categories
    if (permission.includes('delete') || permission.includes('remove'))
      return styles.redTag;
    if (permission.includes('update') || permission.includes('assign'))
      return styles.purpleTag;
    if (permission.includes('create') || permission.includes('add'))
      return styles.blueTag;
    return styles.blueTag; // default color
  };

  const handleRemovePermission = async (
    roleId: number,
    permissionId: number,
  ) => {
    setRemovingPermission({ roleId, permissionId });
    try {
      await removePermissionFromRole(roleId, permissionId);
      // Refresh data after removing permission
      await fetchData();
    } catch (err: any) {
      setError('Failed to remove permission');
      console.error('Error removing permission:', err);
    } finally {
      setRemovingPermission(null);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [permissionsData, rolesData] = await Promise.all([
        getAllPermissions(),
        getAllRoles(),
      ]);
      setPermissions(permissionsData);
      setRoles(rolesData);
    } catch (err: any) {
      setError('Failed to load permissions and roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Transform roles data to match the table format
  const tableData = roles.map((role) => ({
    role: role.name,
    roleId: role.id,
    permissions: role.permissions.map((perm) => ({
      id: perm.id,
      name: perm.name,
    })),
  }));

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.headerRow}>
          <h1 className={styles.title}>Permissions</h1>
        </div>
        <TableLoading
          loading={true}
          rows={5}
          columns={2}
          text="Loading permissions and roles..."
          className={styles.tableWrapper}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.headerRow}>
          <h1 className={styles.title}>Permissions</h1>
        </div>
        <div className={styles.tableWrapper}>
          <p className={styles.error}>Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <h1 className={styles.title}>Permissions</h1>
        <div className={styles.buttonGroup}>
          <button
            className={styles.actionButton}
            onClick={() => setShowNewRoleModal(true)}
          >
            Create New Role
          </button>
          <button
            className={styles.actionButton}
            onClick={() => setShowAddPermissionsModal(true)}
          >
            Add New Permission
          </button>
        </div>
      </div>
      <div className={styles.tableWrapper}>
        <table className={styles.permissionsTable}>
          <thead>
            <tr>
              <th className={styles.roleHeader}>Role</th>
              <th className={styles.permissionHeader}>Permission</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, idx) => (
              <tr key={row.role}>
                <td className={styles.roleCell}>{row.role}</td>
                <td className={styles.permissionCell}>
                  <div className={styles.permissionTagsRow}>
                    {row.permissions.map((perm, i) => (
                      <span
                        key={i}
                        className={`${styles.permissionTag} ${permissionColor(
                          perm.name,
                        )}`}
                      >
                        {perm.name}
                        <span
                          className={styles.closeIcon}
                          onClick={() =>
                            handleRemovePermission(row.roleId, perm.id)
                          }
                          style={{ cursor: 'pointer' }}
                          title="Remove permission"
                        >
                          {removingPermission?.roleId === row.roleId &&
                          removingPermission?.permissionId === perm.id
                            ? '...'
                            : '×'}
                        </span>
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <NewRoleModal
        open={showNewRoleModal}
        onClose={() => setShowNewRoleModal(false)}
        onSave={() => {
          setShowNewRoleModal(false);
          // Refresh data after creating a role
          fetchData();
        }}
      />
      <AddPermissionsModal
        open={showAddPermissionsModal}
        onClose={() => setShowAddPermissionsModal(false)}
        onSave={() => {
          setShowAddPermissionsModal(false);
          // Refresh data after assigning permissions
          fetchData();
        }}
        roles={roles}
        permissions={permissions}
      />
    </div>
  );
};

export default PermissionsPage;
